import { Input } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { useChannelContext } from "@sendbird/uikit-react/Channel/context";
import { useEffect, useRef, useState } from "react";

import { MESSAGE_INPUT_SPACING } from "../../Chat/constants";
import { useToast } from "../../lib";
import { IconButton } from "../components/IconButton";
import {
  useEditingMessage,
  useQuoteMessage,
  useSetEditingMessage,
  useSetQuoteMessage,
} from "./context/EditMessageContext";
import { EditingMessageIndicator } from "./EditingMessageIndicator";
import { QuoteReply } from "./QuoteReply";

interface CustomMessageInputProps {
  prefilledMessage?: string;
}

export function CustomMessageInput({ prefilledMessage }: CustomMessageInputProps) {
  const {
    sendMessage,
    sendFileMessage,
    updateMessage,
    currentGroupChannel,
    setQuoteMessage: setSendbirdQuoteMessage,
  } = useChannelContext();
  const editingMessage = useEditingMessage();
  const setEditingMessage = useSetEditingMessage();
  const quoteMessage = useQuoteMessage();
  const setQuoteMessage = useSetQuoteMessage();

  const [messageInput, setMessageInput] = useState(prefilledMessage ?? "");
  const { showErrorToast } = useToast();

  const isEditing = isDefined(editingMessage);

  useEffect(() => {
    if (isDefined(editingMessage)) {
      setMessageInput(editingMessage.message);
    }
  }, [editingMessage]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const textInputRef = useRef<HTMLInputElement>(null);

  const chatContainer = document.querySelector(
    ".chat-container .sendbird-conversation__messages-padding"
  );

  const canSend = messageInput.trim().length > 0;

  const hideKeyboard = () => {
    textInputRef.current?.blur();
  };

  const handleSendMessage = () => {
    if (!canSend) {
      return;
    }

    try {
      if (isEditing) {
        updateMessage({ messageId: editingMessage.messageId, message: messageInput });
        setEditingMessage(null);
      } else {
        sendMessage({ message: messageInput, ...(isDefined(quoteMessage) && { quoteMessage }) });
        setQuoteMessage(null);
        setSendbirdQuoteMessage(null);
      }
      setMessageInput("");
      void currentGroupChannel?.endTyping?.();
    } catch {
      showErrorToast(
        isEditing
          ? "There was an error updating your message. Please try again later."
          : "There was an error sending your message. Please try again later."
      );
    }
  };

  const handleCancelEdit = () => {
    setEditingMessage(null);
    setMessageInput("");
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const { files } = event.target;

      if (isDefined(files) && files.length > 0) {
        // eslint-disable-next-line prefer-destructuring
        const file = files[0];
        await sendFileMessage(file);
      }
    } catch {
      showErrorToast("There was an error sending your file. Please try again later.");
    }
  };

  const handleClickAttachFile = () => {
    fileInputRef.current?.click();
  };

  const handleInputFocus = () => {
    chatContainer?.addEventListener("touchmove", hideKeyboard, { passive: true, once: true });
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    void currentGroupChannel?.startTyping?.();
    setMessageInput(event.target.value);
  };

  const handleCloseQuoteReply = () => {
    setQuoteMessage(null);
    setSendbirdQuoteMessage(null);
  };

  return (
    <Stack paddingX={MESSAGE_INPUT_SPACING} sx={{ boxShadow: (theme) => theme.shadows[4] }}>
      {isEditing && <EditingMessageIndicator onCancel={handleCancelEdit} />}
      {!isEditing && isDefined(quoteMessage) && (
        <QuoteReply quoteMessage={quoteMessage} onClose={handleCloseQuoteReply} />
      )}

      <Stack spacing={2} direction="row" alignItems="center" paddingTop={MESSAGE_INPUT_SPACING}>
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: "none" }}
          aria-label="file-input"
          onChange={handleFileSelect}
        />
        <Input
          inputRef={textInputRef}
          placeholder={isEditing ? "Edit message" : "Message"}
          value={messageInput}
          aria-label="chat-text-input"
          onEnterPress={handleSendMessage}
          onFocus={handleInputFocus}
          onChange={handleInputChange}
        />
        <IconButton
          iconType="paperclip"
          variant="outlined"
          aria-label="attach-file"
          onClick={handleClickAttachFile}
        />
        <IconButton
          iconType="arrow-up"
          variant="contained"
          aria-label="send"
          disabled={!canSend}
          onClick={handleSendMessage}
        />
      </Stack>
    </Stack>
  );
}
